import { useState, useEffect, useCallback, useRef } from 'react';

export const NETWORK_QUALITY = {
  OFFLINE: 'offline',
  SLOW: 'slow',      // < 2 Mbps - triggers optimization
  FAST: 'fast'       // >= 2 Mbps - normal behavior
};

const DEFAULT_CONFIG = {
  enableSpeedTest: true,
  speedTestInterval: 30000, // Set to 0 to disable periodic testing
  speedTestTimeout: 8000,
  testSizeKB: 50, // Real download size
  slowThresholdMbps: 2, // Threshold for slow/fast classification
  maxRetries: 2,
  runOnce: false // If true, only run speed test once on mount
};

/**
 * Hook to monitor network quality and determine if optimization is needed
 * Uses real HTTP requests to measure actual network performance
 */
export const useNetworkQuality = (config = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const [networkState, setNetworkState] = useState({
    isOnline: navigator.onLine,
    quality: NETWORK_QUALITY.FAST,
    speed: null, // Mbps
    latency: null, // ms
    effectiveType: null,
    downlink: null,
    isLoading: false,
    lastUpdated: null,
    samples: [],
    hasRunOnce: false // Track if initial test has completed
  });

  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Classify connection as slow or fast based on measured speed
  const getQualityFromSpeed = useCallback((speedMbps) => {
    if (speedMbps === null || speedMbps === undefined) {
      return NETWORK_QUALITY.FAST; // Default to fast if unknown
    }
    return speedMbps < finalConfig.slowThresholdMbps ? NETWORK_QUALITY.SLOW : NETWORK_QUALITY.FAST;
  }, [finalConfig.slowThresholdMbps]);

  // Generate test URL with cache busting to force real network request
  const generateTestUrl = useCallback(() => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(7);
    // Use a reliable CDN endpoint that returns predictable data
    return `https://httpbin.org/bytes/${finalConfig.testSizeKB * 1024}?cache=${timestamp}_${random}`;
  }, [finalConfig.testSizeKB]);

  // Perform actual network speed test using real HTTP request
  const performSpeedTest = useCallback(async () => {
    if (!navigator.onLine) return null;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      const testUrl = generateTestUrl();
      const startTime = performance.now();

      // Real HTTP request to measure actual network performance
      const response = await fetch(testUrl, {
        method: 'GET',
        signal,
        // Disable cache to ensure real network measurement
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Wait for complete response body
      await response.arrayBuffer();

      const endTime = performance.now();
      const durationSeconds = (endTime - startTime) / 1000;
      const bytesDownloaded = finalConfig.testSizeKB * 1024;
      const speedBps = bytesDownloaded / durationSeconds;
      const speedMbps = (speedBps * 8) / (1024 * 1024);

      return {
        speed: Math.round(speedMbps * 100) / 100,
        latency: Math.round(endTime - startTime),
        timestamp: Date.now()
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Speed test aborted');
        return null;
      }

      // Network error indicates slow connection
      console.warn('Speed test failed, assuming slow connection:', error.message);
      return {
        speed: 0.5, // Assume very slow on failure
        latency: 5000,
        timestamp: Date.now()
      };
    }
  }, [finalConfig.testSizeKB, generateTestUrl]);

  // Update network state with new measurements
  const updateNetworkState = useCallback(async (forceTest = false) => {
    // Skip if runOnce is enabled and test already completed
    if (finalConfig.runOnce && networkState.hasRunOnce && !forceTest) {
      return;
    }

    setNetworkState(prev => ({ ...prev, isLoading: true }));

    const isOnline = navigator.onLine;
    let newState = {
      isOnline,
      isLoading: false,
      lastUpdated: Date.now(),
      hasRunOnce: true // Mark that we've run at least once
    };

    if (!isOnline) {
      newState.quality = NETWORK_QUALITY.OFFLINE;
      newState.speed = null;
      newState.latency = null;
    } else {
      // Get connection info from browser API if available
      if ('connection' in navigator) {
        const connection = navigator.connection;
        newState.effectiveType = connection.effectiveType;
        newState.downlink = connection.downlink;
      }

      // Perform real speed test when enabled
      if (finalConfig.enableSpeedTest) {
        const speedTestResult = await performSpeedTest();

        if (speedTestResult) {
          newState.speed = speedTestResult.speed;
          newState.latency = speedTestResult.latency;
          newState.quality = getQualityFromSpeed(speedTestResult.speed);

          // Keep sample history for averaging
          setNetworkState(prev => {
            const newSamples = [...prev.samples, speedTestResult].slice(-5);
            return {
              ...prev,
              ...newState,
              samples: newSamples
            };
          });
          return;
        }
      }

      // Fallback classification using browser connection API
      if (newState.effectiveType) {
        switch (newState.effectiveType) {
          case 'slow-2g':
          case '2g':
          case '3g':
            newState.quality = NETWORK_QUALITY.SLOW;
            break;
          case '4g':
          default:
            newState.quality = NETWORK_QUALITY.FAST;
        }
      }
    }

    setNetworkState(prev => ({ ...prev, ...newState }));
  }, [finalConfig.enableSpeedTest, finalConfig.runOnce, networkState.hasRunOnce, performSpeedTest, getQualityFromSpeed]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => updateNetworkState();
    const handleOffline = () => {
      setNetworkState(prev => ({
        ...prev,
        isOnline: false,
        quality: NETWORK_QUALITY.OFFLINE,
        lastUpdated: Date.now()
      }));
    };

    const handleConnectionChange = () => {
      // Debounce to avoid excessive updates
      setTimeout(updateNetworkState, 1000);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', handleConnectionChange);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if ('connection' in navigator) {
        navigator.connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [updateNetworkState]);

  // Periodic speed testing
  useEffect(() => {
    if (finalConfig.enableSpeedTest) {
      updateNetworkState(); // Initial test

      // Only set interval if speedTestInterval > 0 and runOnce is false
      if (finalConfig.speedTestInterval > 0 && !finalConfig.runOnce) {
        intervalRef.current = setInterval(updateNetworkState, finalConfig.speedTestInterval);
      }

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }
  }, [finalConfig.enableSpeedTest, finalConfig.speedTestInterval, finalConfig.runOnce, updateNetworkState]);

  // Primary method: determines if app should use optimization mode
  const isSlowConnection = useCallback(() => {
    return networkState.quality === NETWORK_QUALITY.SLOW ||
           networkState.quality === NETWORK_QUALITY.OFFLINE;
  }, [networkState.quality]);

  const isFastConnection = useCallback(() => {
    return networkState.quality === NETWORK_QUALITY.FAST;
  }, [networkState.quality]);

  const getAverageSpeed = useCallback(() => {
    if (networkState.samples.length === 0) return null;

    const totalSpeed = networkState.samples.reduce((sum, sample) => sum + sample.speed, 0);
    return Math.round((totalSpeed / networkState.samples.length) * 100) / 100;
  }, [networkState.samples]);

  const testSpeed = useCallback(async () => {
    await updateNetworkState(true); // Force test even if runOnce is enabled
  }, [updateNetworkState]);

  return {
    // Core state
    ...networkState,

    // Calculated values
    averageSpeed: getAverageSpeed(),

    // Primary decision method for app behavior
    isSlowConnection, // Use this to decide: optimization vs normal behavior
    isFastConnection,
    testSpeed,

    config: finalConfig
  };
};